import './styles.scss';

import axios from 'axios';
import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import Lightbox from 'react-image-lightbox';
import { Card, CardHeader, Modal, Table } from 'reactstrap';

import Breadcrumb from '@/components/common/breadcrumb.component';
import Icon from '@/components/icon';
import ModalDialog from '@/components/modal-dialog';
import Button from '@/components/overrides/button';
import Pagination from '@/components/pagination';
import TouchSpin from '@/components/touch-spin';
import { Constants } from '@/constants';

let nextMarker = null;

const PAGE_SIZE = 30;

const markers = localStorage.getItem('cigar-reference-image-s3-markers')
  ? JSON.parse(localStorage.getItem('cigar-reference-image-s3-markers'))
  : {};

function CigarBandRecognition() {
  const [cigars, setCigars] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState('training');
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedCigarId, setSelectedCigarId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [imageCount, setImageCount] = useState(0);

  const [pageToSet, setPageToSet] = useState(null);
  const [showPagePickerModal, setShowPagePickerModal] = useState(false);
  const [urlToImport, setUrlToImport] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);

  const [embeddingCigarId, setEmbeddingCigarId] = useState(null);
  const [similarityCigarId, setSimilarityCigarId] = useState(null);

  const getRecords = () => {
    axios
      .get(`${Constants.apiPath}/actions/cigars/images/objects`, {
        params: {
          next_marker: markers[currentPage],
        },
      })
      .then((res) => {
        if (res.data?.folders) {
          setCigars(
            res.data.folders.map((folder, index) => ({
              id: folder,
              references: {
                training: res.data.training_data[index] || [],
                tests: res.data.test_data[index] || [],
              },
            }))
          );

          if (res.data.marker) {
            nextMarker = res.data.marker;
            markers[currentPage + 1] = nextMarker;

            localStorage.setItem(
              'cigar-reference-image-s3-markers',
              JSON.stringify(markers)
            );
          }
        }
      });
  };

  const deleteImage = (url) => {
    const parts = url.split('/');

    axios
      .delete(`${Constants.apiPath}/actions/cigars/${selectedCigarId}/images`, {
        params: {
          key: parts[parts.length - 1].split('?')[0],
          type: selectedType,
        },
      })
      .then(() => {
        const updatedImages = selectedImages.filter((item) => item !== url);

        setSelectedImages(updatedImages);

        setCigars(
          cigars.map((cigar) => {
            if (cigar.id === selectedCigarId) {
              cigar.references[selectedType] = updatedImages;
            }

            return cigar;
          })
        );

        ModalDialog.close();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    axios
      .get(`${Constants.apiPath}/actions/cigars/images/stats`, {
        params: {
          next_marker: markers[currentPage],
        },
      })
      .then((res) => {
        setTotalCount(res.data.label_count);
        setImageCount(res.data.image_count);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  useEffect(() => {
    getRecords();
  }, [currentPage]);

  const renderImageGrid = (cigarId, images, type) => {
    return (
      <span
        style={{ cursor: 'pointer' }}
        onClick={() => {
          setSelectedType(type);
          setSelectedImages(images);
          setShowModal(true);
          setSelectedCigarId(cigarId);
        }}
      >
        {images?.slice(0, 5).map((image, index) => (
          <span key={index}>
            <img
              src={image}
              alt="Cigar"
              style={{
                height: 90,
                width: 90,
                objectFit: 'contain',
                border: '1px solid #eaeaea',
                borderRadius: 6,
                margin: 3,
              }}
            />
          </span>
        ))}

        {images?.length > 0 && <Icon name="chevron-right" />}
        {images?.length === 0 && <Icon name="plus" />}
      </span>
    );
  };

  const searchGoogle = (cigarId) => {
    axios
      .get(`${Constants.clientPath}/cigars/${cigarId}`)
      .then((res) => {
        if (res.data?.full_name) {
          window.open(
            `https://www.google.com/search?tbm=isch&q=${res.data.full_name}`,
            '_blank'
          );
        }
      })
      .catch((e) => {
        console.log(e.message);
        alert('Unable to get cigar details');
      });
  };

  const generateEmbeddings = async (cigar) => {
    try {
      setEmbeddingCigarId(cigar.id);

      await axios.post(
        `${Constants.apiPath}/actions/cigars/${cigar.id}/embeddings`,
        {
          images: cigar.references.training,
        }
      );

      ModalDialog.show({
        title: 'Embeddings generated',
        message: `Reference embeddings were generated for cigar ${cigar.id}.`,
        type: 'success',
      });
    } catch (err) {
      console.error(err);

      ModalDialog.show({
        title: 'Unable to generate embeddings',
        message: 'There was a problem generating the reference embeddings.',
        type: 'error',
      });
    } finally {
      setEmbeddingCigarId(null);
    }
  };

  const findSimilar = async (cigar) => {
    try {
      setSimilarityCigarId(cigar.id);

      const res = await axios.post(
        `${Constants.apiPath}/actions/cigars/${cigar.id}/embeddings/search`,
        {
          images: cigar.references.tests,
        }
      );

      console.log('Similarity results:', res.data);

      // TODO:
      // Display these in a modal with:
      // cigar_id
      // similarity / confidence
      // matching reference image
    } catch (err) {
      console.error(err);

      ModalDialog.show({
        title: 'Unable to find similar cigars',
        message: 'There was a problem running the similarity search.',
        type: 'error',
      });
    } finally {
      setSimilarityCigarId(null);
    }
  };

  const renderModal = () => (
    <Modal
      isOpen={showModal}
      onClosed={() => setShowModal(false)}
      toggle={() => setShowModal(false)}
      className="modal-dialog-lg"
      style={{ maxWidth: 878 }}
    >
      <div className="modal-header">
        <h5 className="modal-title" id="cigar-modal-title">
          {selectedType === 'tests'
            ? 'Edit Test Images'
            : 'Edit Reference Images'}
        </h5>
      </div>

      <div className="modal-body" style={{ textAlign: 'center' }}>
        {selectedImages?.slice(0, 20).map((image, index) => (
          <span
            key={index}
            style={{
              position: 'relative',
              display: 'inline-flex',
              height: 200,
              width: 200,
              cursor: 'pointer',
              margin: 5,
            }}
          >
            <div className="image-overlay">
              <Icon
                name="trash"
                className="delete-image-icon"
                onClick={() => {
                  ModalDialog.show({
                    title: 'Delete image?',
                    message: 'Are you sure you want to delete this image?',
                    type: 'error',
                    buttons: [
                      {
                        label: 'Cancel',
                        onClick: () => {
                          ModalDialog.close();
                        },
                      },
                      {
                        label: 'Delete',
                        style: { color: 'rgb(239, 81, 100)' },
                        onClick: () => deleteImage(image),
                      },
                    ],
                  });
                }}
              />

              <Icon
                name="maximize2"
                className="expand-icon"
                onClick={() => {
                  setSelectedIndex(index);
                  setShowLightbox(true);
                }}
              />
            </div>

            <img
              src={image}
              alt="Cigar"
              style={{
                height: 200,
                width: 200,
                objectFit: 'contain',
                border: '1px solid #eaeaea',
                borderRadius: 12,
              }}
            />
          </span>
        ))}

        <div id="add-images-dropzone-wrapper">
          <Dropzone
            id="add-images-dropzone"
            config={{
              postUrl: 'no-url',
              iconFiletypes: ['.jpg', '.png'],
            }}
            djsConfig={{
              dictDefaultMessage: 'Drop images here',
            }}
          >
            {({ getRootProps, getInputProps }) => (
              <section className="dropzone">
                <div style={{ height: '100%' }} {...getRootProps()}>
                  <input {...getInputProps()} />
                  <p>Drop reference image here</p>
                </div>
              </section>
            )}
          </Dropzone>
        </div>

        <div style={{ textAlign: 'center', margin: 20 }}>- or -</div>

        <div style={{ textAlign: 'left', margin: 12 }}>
          <label>Image from URL</label>

          <div className="input-group">
            <input
              className="form-control"
              type="text"
              name="image-url"
              placeholder="https://"
              value={urlToImport}
              onChange={(e) => setUrlToImport(e.target.value)}
            />

            <div className="input-group-append">
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => {
                  axios
                    .put(
                      `${Constants.apiPath}/actions/cigars/${selectedCigarId}/images`,
                      {
                        image_url: urlToImport,
                        type: selectedType,
                      }
                    )
                    .then(() => {
                      const updatedImages = selectedImages.concat(urlToImport);

                      setSelectedImages(updatedImages);

                      setCigars(
                        cigars.map((cigar) => {
                          if (cigar.id === selectedCigarId) {
                            cigar.references[selectedType] = updatedImages;
                          }

                          return cigar;
                        })
                      );

                      setUrlToImport('');
                    })
                    .catch((err) => {
                      console.error(err);
                    });
                }}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <Button onClick={() => setShowModal(false)}>Close</Button>
      </div>
    </Modal>
  );

  const renderPagePickerModal = () => (
    <Modal
      isOpen={showPagePickerModal}
      onClosed={() => setShowPagePickerModal(false)}
      toggle={() => setShowPagePickerModal(false)}
    >
      <div className="modal-header">
        <h5 className="modal-title">Select Page</h5>
      </div>

      <div className="modal-body">
        <TouchSpin
          max={Math.ceil(totalCount / PAGE_SIZE)}
          min={1}
          step={1}
          value={currentPage || pageToSet}
          onChange={(value) => setPageToSet(value)}
        />
      </div>

      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-light"
          onClick={() => setShowPagePickerModal(false)}
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setCurrentPage(pageToSet);
            setShowPagePickerModal(false);
          }}
        >
          Go
        </button>
      </div>
    </Modal>
  );

  return (
    <div>
      <Breadcrumb
        title="Cigar Band Recognition"
        label="Band Recognition"
        parent="Cigar Manager"
      />

      <div className="container-fluid">
        <Card>
          <CardHeader>
            <span style={{ float: 'left' }}>
              {`Total reference images: ${imageCount}`}
              <br />
              <small style={{ opacity: 0.65 }}>
                Embeddings are generated and managed by the Boxpressd AI
                service.
              </small>
            </span>
          </CardHeader>

          <div className="table-responsive">
            <Table size="lg">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Reference Images</th>
                  <th scope="col">Test Images</th>
                  <th scope="col">AI Actions</th>
                </tr>
              </thead>

              <tbody>
                {cigars.map((cigar) => (
                  <tr key={cigar.id}>
                    <td>
                      <a
                        href={`/cigars/manager?id=${cigar.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {cigar.id}
                      </a>

                      <Icon
                        name="search"
                        style={{
                          marginLeft: 10,
                          cursor: 'pointer',
                        }}
                        size={14}
                        onClick={() => searchGoogle(cigar.id)}
                      />
                    </td>

                    <td>
                      {renderImageGrid(
                        cigar.id,
                        cigar.references.training,
                        'training'
                      )}
                    </td>

                    <td>
                      {renderImageGrid(
                        cigar.id,
                        cigar.references.tests,
                        'tests'
                      )}
                    </td>

                    <td>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          minWidth: 160,
                        }}
                      >
                        <Button
                          color="brand"
                          disabled={
                            embeddingCigarId === cigar.id ||
                            !cigar.references.training?.length
                          }
                          onClick={() => generateEmbeddings(cigar)}
                        >
                          {embeddingCigarId === cigar.id
                            ? 'Generating...'
                            : 'Generate Embeddings'}
                        </Button>

                        <Button
                          outline
                          color="light"
                          disabled={
                            similarityCigarId === cigar.id ||
                            !cigar.references.tests?.length
                          }
                          onClick={() => findSimilar(cigar)}
                        >
                          {similarityCigarId === cigar.id
                            ? 'Searching...'
                            : 'Find Similar'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div
            style={{
              margin: 20,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Pagination
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={(page) => setCurrentPage(page)}
            />

            <span
              className="pagination-container"
              onClick={() => setShowPagePickerModal(true)}
            >
              <span className="pagination-item">Select Page</span>
            </span>
          </div>
        </Card>
      </div>

      {renderModal()}
      {renderPagePickerModal()}

      {showLightbox && (
        <Lightbox
          mainSrc={selectedImages[selectedIndex]}
          nextSrc={selectedImages[(selectedIndex + 1) % selectedImages.length]}
          prevSrc={
            selectedImages[
              (selectedIndex + selectedImages.length - 1) %
                selectedImages.length
            ]
          }
          imageTitle={`${selectedIndex + 1}/${selectedImages.length}`}
          onCloseRequest={() => setShowLightbox(false)}
          onMovePrevRequest={() =>
            setSelectedIndex(
              (selectedIndex + selectedImages.length - 1) %
                selectedImages.length
            )
          }
          onMoveNextRequest={() =>
            setSelectedIndex((selectedIndex + 1) % selectedImages.length)
          }
        />
      )}
    </div>
  );
}

export default CigarBandRecognition;
